import dayjs, { Dayjs } from 'dayjs';
import { ISO_DATE_FORMAT, US_DATE_SLASH_FORMAT } from 'src/constants';
import { ComponentTypes, DyProductSource, IComponent } from 'src/interfaces';
import { lang } from 'src/utils';

import { IScheduledSectionComponent } from '../../ScheduleComponentPage.interface';

export const onDateRangeValidate = (
  value: (Dayjs | null)[],
  isOwnerOperator?: boolean,
  mainStartDateStr?: string | null,
  mainEndDateStr?: string | null,
  componentExpiredDateStr?: string | null,
): string | false => {
  const [startDate, endDate] = value;
  if (!startDate) return lang.startDateRequired;
  if (!startDate.isValid()) return lang.errors.invalidDateFormat('start_date');
  if (
    !endDate &&
    componentExpiredDateStr &&
    startDate.isAfter(dayjs(componentExpiredDateStr))
  )
    return lang.startDateExceedError;
  if (!endDate && isOwnerOperator) return lang.endDateRequired;
  if (endDate) {
    if (!endDate.isValid()) return lang.errors.invalidDateFormat('end_date');
    if (startDate.isAfter(endDate)) return lang.pastDateValidationError;
    if (
      componentExpiredDateStr &&
      endDate.isAfter(dayjs(componentExpiredDateStr))
    )
      return lang.endDateExceedError;
    if (mainStartDateStr) {
      const mainStartDate = dayjs(mainStartDateStr);
      if (mainStartDate.isValid() && startDate.isBefore(mainStartDate, 'day')) {
        return lang.startDateOutsideMainRange;
      }
    }
    if (mainEndDateStr) {
      const mainEndDate = dayjs(mainEndDateStr);
      if (mainEndDate.isValid() && endDate.isAfter(mainEndDate, 'day')) {
        return lang.endDateOutsideMainRange;
      }
    }
  }

  return false;
};

export const validateScheduleComponentDate = (
  dateRange: (Dayjs | null)[],
  disabledStartDates: string[],
  isOwnerOperator?: boolean,
  mainStartDate?: string | null,
  mainEndDate?: string | null,
  component?: IComponent,
): string | false => {
  const validationError = onDateRangeValidate(
    dateRange,
    isOwnerOperator,
    mainStartDate,
    mainEndDate,
    component?.end_date,
  );
  if (validationError) return validationError;

  const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
  const duplicateCount = disabledStartDates.filter(
    (d) => d === startDate,
  ).length;

  if (duplicateCount > 1) {
    return lang.startDateConflict;
  }

  return false;
};

export const getDateDisabledChecker = (
  type: 'start' | 'end',
  component: IComponent,
  disabledStartDates?: string[],
) => {
  const isStartDateDisabled = (date: Dayjs) =>
    !!disabledStartDates?.includes(date.format(ISO_DATE_FORMAT)) ||
    (component.end_date ? date.isAfter(component.end_date) : false);

  const isEndDateDisabled = (date: Dayjs) =>
    component.end_date ? date.isAfter(component.end_date) : false;

  return (date: Dayjs) =>
    type === 'start' ? isStartDateDisabled(date) : isEndDateDisabled(date);
};

export const getDateValue = (
  overrideObj: any,
  overrideKey: string,
  fallback: any,
) => {
  if (overrideObj) {
    return overrideObj[overrideKey] ? dayjs(overrideObj[overrideKey]) : null;
  }

  return fallback ? dayjs(fallback) : null;
};

export const getComponentValue = (
  baseComponent?: IComponent,
  overrideComponent?: IComponent,
) => {
  return {
    type:
      overrideComponent?.component_type?.name ??
      baseComponent?.component_type?.name,
    config: overrideComponent?.config ?? baseComponent?.config,
    name: overrideComponent?.name ?? baseComponent?.name,
    width: overrideComponent?.width ?? baseComponent?.width,
    height: overrideComponent?.height ?? baseComponent?.height,
    theme: overrideComponent?.theme?.name ?? baseComponent?.theme?.name,
  };
};

export const isAllowedComponentType = (
  selectedComponent: IScheduledSectionComponent,
) => {
  const componentConfig = selectedComponent?.component?.config ?? {};
  const componentTypeName =
    selectedComponent?.component?.component_type?.name ?? '';

  const isSingleShelfWithAllowedSource =
    componentTypeName === ComponentTypes.SINGLE_SHELF &&
    componentConfig?.productsSource !== DyProductSource.DYR &&
    componentConfig?.productsSource !== DyProductSource.DYS;

  const enabled =
    componentTypeName === ComponentTypes.PROMOTIONAL_SPACE ||
    componentTypeName === ComponentTypes.LISTING_WITH_OPTIONAL_VISUALS ||
    componentTypeName === ComponentTypes.LISTING_VISUALS_GALLERY ||
    isSingleShelfWithAllowedSource;

  return enabled;
};

export const formatComponentSubtitle = (
  startDate?: string | null,
  endDate?: string | null,
): string => {
  const formattedStartDate = startDate
    ? dayjs(startDate).format(US_DATE_SLASH_FORMAT)
    : '';
  const formattedEndDate = endDate
    ? dayjs(endDate).format(US_DATE_SLASH_FORMAT)
    : '';

  if (formattedStartDate && formattedEndDate) {
    return `${formattedStartDate} - ${formattedEndDate}`;
  } else if (formattedStartDate) {
    return formattedStartDate;
  } else if (formattedEndDate) {
    return `- ${formattedEndDate}`;
  }

  return '';
};
